import { api } from './api';

export interface MaintenanceStatus {
  maintenance_mode: boolean;
  maintenance_message: string;
  allowed_ips?: string[];
  message?: string;
  timestamp?: string;
}

export interface MaintenanceToggleData {
  enable: boolean;
  message?: string;
  allowed_ips?: string[];
}

class MaintenanceService {
  private maintenanceStatus: MaintenanceStatus | null = null;
  private lastChecked: number = 0;
  private readonly CACHE_DURATION = 30000; // 30 seconds

  async getMaintenanceStatus(): Promise<MaintenanceStatus> {
    const now = Date.now();

    // Return cached status if it's still fresh
    if (this.maintenanceStatus && (now - this.lastChecked) < this.CACHE_DURATION) {
      return this.maintenanceStatus;
    }

    try {
      const response = await api.get<MaintenanceStatus>('/api/maintenance/status');

      if (response.success && response.data) {
        this.maintenanceStatus = {
          ...response.data,
          timestamp: response.timestamp ?? new Date().toISOString()
        };
        this.lastChecked = now;
        return this.maintenanceStatus;
      }

      throw new Error(response.error || 'Nu am putut obține starea de mentenanță.');
    } catch (error) {
      // If we can't reach the API, assume no maintenance mode
      console.warn('Failed to check maintenance status:', error);
      return {
        maintenance_mode: false,
        maintenance_message: '',
        timestamp: new Date().toISOString()
      };
    }
  }

  async toggleMaintenanceMode(data: MaintenanceToggleData): Promise<MaintenanceStatus> {
    try {
      const response = await api.post<MaintenanceStatus>('/api/admin/maintenance/toggle', data);

      // Clear cache to force refresh
      this.maintenanceStatus = null;
      this.lastChecked = 0;

      if (response.success && response.data) {
        return {
          ...response.data,
          timestamp: response.timestamp ?? new Date().toISOString()
        };
      }

      throw new Error(response.error || 'Nu am putut actualiza modul de mentenanță.');
    } catch (error) {
      console.error('Failed to toggle maintenance mode:', error);
      throw error;
    }
  }

  clearCache(): void {
    this.maintenanceStatus = null;
    this.lastChecked = 0;
  }

  isMaintenanceMode(): boolean {
    return this.maintenanceStatus?.maintenance_mode ?? false;
  }

  getMaintenanceMessage(): string {
    return this.maintenanceStatus?.maintenance_message ?? '';
  }
}

export const maintenanceService = new MaintenanceService();
export {};
